import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { ApiError } from '../../lib/apiClient';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { EmptyState } from '../atoms/EmptyState';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { Dialog } from '../ui/Dialog/Dialog';
import {
  askQuestion,
  CHART_TYPES,
  deleteSavedQuery,
  listSavedQueries,
  runSavedQuery,
  runSql,
  saveQuery,
  savedQueriesKey,
  type AnalyticsResult,
  type AnalyticsScope,
  type ChartSpec,
  type ChartType,
  type SavedQuery,
} from '../../services/analyticsService';
import { resolveChart, toCsv } from './analyticsFormat';
import { ResultChart } from './ResultChart';
import { ResultTable } from './ResultTable';
import './AnalyticsWorkbench.css';

export interface AnalyticsWorkbenchProps {
  scope: AnalyticsScope;
}

interface Current {
  title: string;
  explanation: string | null;
  question: string | null;
  sql: string;
  chart: ChartSpec;
  result: AnalyticsResult | null;
}

interface Failure {
  /** i18n key of the reason. */
  messageKey: string;
  /** Technical detail (database / guard message) shown next to the SQL. */
  detail?: string;
}

const QUERY_CODES = new Set(['SQL_EMPTY', 'SQL_TOO_LONG', 'SQL_SYNTAX', 'SQL_NOT_ALLOWED', 'SQL_FAILED', 'SQL_TIMEOUT', 'NO_QUERY']);

function failureOf(err: unknown): Failure {
  if (err instanceof ApiError) {
    const body = err.data as { error?: { code?: string; message?: string } } | undefined;
    const code = body?.error?.code ?? err.code;
    if (QUERY_CODES.has(code)) return { messageKey: `analytics.errors.${code}`, detail: body?.error?.message };
    if (code === 'AI_BUDGET_EXCEEDED') return { messageKey: 'analytics.errors.AI_BUDGET_EXCEEDED' };
    if (err.status === 429) return { messageKey: 'analytics.errors.rateLimited' };
    if (err.status === 503 || err.status === 502) return { messageKey: 'analytics.errors.aiUnavailable' };
    if (err.status === 403) return { messageKey: 'analytics.errors.forbidden' };
    if (err.status === 0) return { messageKey: 'analytics.errors.network' };
  }
  return { messageKey: 'analytics.errors.generic' };
}

function downloadCsv(result: AnalyticsResult, title: string): void {
  const blob = new Blob([toCsv(result)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^\p{L}\p{N}_-]+/gu, '_').slice(0, 60) || 'analytics'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Analytics workbench: ask a question in plain language → the AI writes a SQL
 * query → the result as table and chart. The SQL can be edited and re-run (no AI
 * cost), the chart type / axes changed, the result downloaded as CSV and saved
 * for later re-runs. `scope` = own data or (admin) the whole database.
 */
export function AnalyticsWorkbench({ scope }: AnalyticsWorkbenchProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState('');
  const [questionError, setQuestionError] = useState<string | undefined>();
  const [current, setCurrent] = useState<Current | null>(null);
  const [sqlDraft, setSqlDraft] = useState('');
  const [failure, setFailure] = useState<Failure | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveTitleError, setSaveTitleError] = useState<string | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<SavedQuery | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);

  const savedQuery = useQuery({ queryKey: savedQueriesKey(scope), queryFn: () => listSavedQueries(scope) });

  const askMutation = useMutation({
    mutationFn: (q: string) => askQuestion(scope, q),
    onSuccess: (answer, q) => {
      setCurrent({ title: answer.title, explanation: answer.explanation, question: q, sql: answer.sql, chart: answer.chart, result: answer.result });
      setSqlDraft(answer.sql);
      setFailure(answer.error ? { messageKey: `analytics.errors.${QUERY_CODES.has(answer.error.code) ? answer.error.code : 'SQL_FAILED'}`, detail: answer.error.detail } : null);
    },
    onError: (err) => setFailure(failureOf(err)),
  });

  const runMutation = useMutation({
    mutationFn: (sql: string) => runSql(scope, sql),
    onSuccess: (run) => {
      setCurrent((c) => ({ title: c?.title ?? t('analytics.result.customTitle'), explanation: c?.explanation ?? null, question: c?.question ?? null, chart: c?.chart ?? { type: 'table' }, sql: run.sql, result: run.result }));
      setSqlDraft(run.sql);
      setFailure(null);
    },
    onError: (err) => setFailure(failureOf(err)),
  });

  const runSavedMutation = useMutation({
    mutationFn: (saved: SavedQuery) => runSavedQuery(scope, saved.id),
    onMutate: (saved) => {
      setCurrent({ title: saved.title, explanation: null, question: saved.question, sql: saved.sql, chart: saved.chart, result: null });
      setSqlDraft(saved.sql);
      setFailure(null);
    },
    onSuccess: (run) => setCurrent((c) => (c ? { ...c, sql: run.sql, result: run.result } : c)),
    onError: (err) => setFailure(failureOf(err)),
  });

  const saveMutation = useMutation({
    mutationFn: () => saveQuery(scope, { title: saveTitle.trim(), question: current?.question ?? null, sql: sqlDraft, chart: current?.chart ?? { type: 'table' } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedQueriesKey(scope) });
      setSaveOpen(false);
      setSavedNotice(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSavedQuery(scope, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedQueriesKey(scope) });
      setDeleteTarget(null);
    },
  });

  const busy = askMutation.isPending || runMutation.isPending || runSavedMutation.isPending;

  function handleAsk(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const q = question.trim();
    if (q.length < 3) {
      setQuestionError(t('analytics.ask.questionRequired'));
      return;
    }
    setQuestionError(undefined);
    setSavedNotice(false);
    askMutation.mutate(q);
  }

  function setChart(patch: Partial<ChartSpec>): void {
    setCurrent((c) => (c ? { ...c, chart: { ...c.chart, ...patch } } : c));
  }

  const result = current?.result ?? null;
  const resolved = current && result ? resolveChart(current.chart, result) : null;
  const numericColumns = result ? result.columns.filter((c) => c.kind === 'number').map((c) => c.name) : [];

  return (
    <>
      <Section title={t(scope === 'admin' ? 'analytics.ask.titleAdmin' : 'analytics.ask.title')}>
        <Card padding="lg">
          <form onSubmit={handleAsk} noValidate>
            <Stack gap="4">
              <Textarea
                id={`analytics-question-${scope}`}
                label={t('analytics.ask.label')}
                helperText={t(scope === 'admin' ? 'analytics.ask.helpAdmin' : 'analytics.ask.help')}
                placeholder={t('analytics.ask.placeholder')}
                value={question}
                rows={3}
                maxLength={1000}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setQuestion(e.target.value)}
                error={questionError}
              />
              <Inline gap="3" justify="end" wrap>
                <Text variant="muted" size="sm">{t('analytics.ask.cost')}</Text>
                <Button type="submit" variant="primary" disabled={busy}>
                  {askMutation.isPending ? t('analytics.ask.asking') : t('analytics.ask.submit')}
                </Button>
              </Inline>
            </Stack>
          </form>
        </Card>
      </Section>

      {busy && (
        <LiveRegion busy>
          <Text variant="muted">{askMutation.isPending ? t('analytics.ask.asking') : t('analytics.result.running')}</Text>
        </LiveRegion>
      )}

      {current && (
        <Section title={current.title}>
          <Stack gap="4">
            {current.explanation && <Text>{current.explanation}</Text>}

            {failure && (
              <Alert tone="danger" title={t(failure.messageKey)} onDismiss={() => setFailure(null)} dismissLabel={t('analytics.errors.dismiss')}>
                {failure.detail && <code className="analytics-workbench__detail">{failure.detail}</code>}
              </Alert>
            )}
            {savedNotice && (
              <Alert tone="success" live="polite" compact onDismiss={() => setSavedNotice(false)} dismissLabel={t('analytics.errors.dismiss')}>
                {t('analytics.saved.savedNotice')}
              </Alert>
            )}

            <Card padding="lg">
              <Stack gap="3">
                <Textarea
                  id={`analytics-sql-${scope}`}
                  label={t('analytics.sql.label')}
                  helperText={t('analytics.sql.help')}
                  value={sqlDraft}
                  rows={6}
                  spellCheck={false}
                  textareaClassName="analytics-workbench__sql"
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setSqlDraft(e.target.value)}
                />
                <Inline gap="2" justify="end" wrap>
                  <Button type="button" variant="secondary" disabled={busy || sqlDraft.trim() === ''} onClick={() => runMutation.mutate(sqlDraft)}>
                    {t('analytics.sql.run')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={sqlDraft.trim() === ''}
                    onClick={() => {
                      setSaveTitle(current.title);
                      setSaveTitleError(undefined);
                      saveMutation.reset();
                      setSaveOpen(true);
                    }}
                  >
                    {t('analytics.saved.save')}
                  </Button>
                  {result && (
                    <Button type="button" variant="ghost" onClick={() => downloadCsv(result, current.title)}>
                      {t('analytics.result.csv')}
                    </Button>
                  )}
                </Inline>
              </Stack>
            </Card>

            {result && resolved && (
              <Card padding="lg">
                <Stack gap="4">
                  <LiveRegion visuallyHidden>
                    {t('analytics.result.summary', { count: result.rowCount, ms: result.durationMs })}
                  </LiveRegion>
                  <Text variant="muted" size="sm">
                    {t('analytics.result.summary', { count: result.rowCount, ms: result.durationMs })}
                    {result.truncated && ` ${t('analytics.result.truncated')}`}
                  </Text>
                  {result.rowCount === 0 ? (
                    <Text>{t('analytics.result.empty')}</Text>
                  ) : (
                    <>
                      <div className="analytics-workbench__controls">
                        <Select
                          id={`analytics-chart-type-${scope}`}
                          label={t('analytics.chart.type')}
                          value={resolved.type}
                          onChange={(e: ChangeEvent<HTMLSelectElement>) => setChart({ type: e.target.value as ChartType })}
                          options={CHART_TYPES.map((type) => ({ value: type, label: t(`analytics.chart.types.${type}`) }))}
                        />
                        {resolved.type !== 'table' && (
                          <>
                            <Select
                              id={`analytics-chart-x-${scope}`}
                              label={t('analytics.chart.x')}
                              value={resolved.x ?? ''}
                              onChange={(e: ChangeEvent<HTMLSelectElement>) => setChart({ x: e.target.value })}
                              options={result.columns.map((c) => ({ value: c.name, label: c.name }))}
                            />
                            <Select
                              variant="multiple"
                              id={`analytics-chart-y-${scope}`}
                              label={t('analytics.chart.y')}
                              values={resolved.y}
                              onValuesChange={(values) => setChart({ y: values })}
                              options={numericColumns.filter((n) => n !== resolved.x).map((n) => ({ value: n, label: n }))}
                              maxSelected={resolved.type === 'pie' || resolved.type === 'scatter' ? 1 : 5}
                              placeholder={t('analytics.chart.yPlaceholder')}
                            />
                          </>
                        )}
                      </div>
                      {resolved.type !== 'table' && resolved.x && resolved.y.length > 0 && (
                        <ResultChart result={result} type={resolved.type} x={resolved.x} y={resolved.y} label={current.title} />
                      )}
                      {resolved.type !== 'table' && resolved.y.length === 0 && (
                        <Text variant="muted">{t('analytics.chart.noNumeric')}</Text>
                      )}
                      <ResultTable result={result} caption={t('analytics.result.tableCaption', { title: current.title })} />
                    </>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </Section>
      )}

      {!current && failure && (
        <Alert tone="danger" title={t(failure.messageKey)} onDismiss={() => setFailure(null)} dismissLabel={t('analytics.errors.dismiss')}>
          {failure.detail && <code className="analytics-workbench__detail">{failure.detail}</code>}
        </Alert>
      )}

      <Section title={t('analytics.saved.title')}>
        {savedQuery.isLoading ? (
          <LiveRegion>
            <Text variant="muted">{t('analytics.saved.loading')}</Text>
          </LiveRegion>
        ) : savedQuery.isError ? (
          <LoadErrorState title={t('analytics.saved.loadError')} onRetry={() => void savedQuery.refetch()} />
        ) : (savedQuery.data ?? []).length === 0 ? (
          <EmptyState
            icon={<BarChart3 size={48} strokeWidth={1.5} />}
            headline={t('analytics.saved.emptyHeadline')}
            description={t('analytics.saved.empty')}
            actionLabel={t('analytics.saved.emptyAction')}
            actionVariant="secondary"
            headingLevel={3}
            onAction={() => document.getElementById(`analytics-question-${scope}`)?.focus()}
          />
        ) : (
          <ul className="analytics-workbench__saved">
            {(savedQuery.data ?? []).map((saved) => (
              <li key={saved.id} className="analytics-workbench__saved-item">
                <Stack gap="2">
                  <Heading level={3} className="analytics-workbench__saved-title">{saved.title}</Heading>
                  {saved.question && <Text variant="muted" size="sm">{saved.question}</Text>}
                  <Inline gap="2" wrap>
                    <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => runSavedMutation.mutate(saved)}
                      aria-label={t('analytics.saved.runAria', { title: saved.title })}>
                      {t('analytics.saved.run')}
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setDeleteTarget(saved)}
                      aria-label={t('analytics.saved.deleteAria', { title: saved.title })}>
                      {t('analytics.saved.delete')}
                    </Button>
                  </Inline>
                </Stack>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Dialog isOpen={saveOpen} onClose={() => setSaveOpen(false)} title={t('analytics.saved.dialogTitle')} data-testid="analytics-save-dialog">
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (saveTitle.trim() === '') {
              setSaveTitleError(t('analytics.saved.titleRequired'));
              return;
            }
            saveMutation.mutate();
          }}
        >
          <Stack gap="4">
            {saveMutation.isError && (
              <Alert tone="danger">
                {saveMutation.error instanceof ApiError && saveMutation.error.status === 409
                  ? t('analytics.saved.limit')
                  : t('analytics.saved.saveError')}
              </Alert>
            )}
            <Input
              id={`analytics-save-title-${scope}`}
              label={t('analytics.saved.titleLabel')}
              value={saveTitle}
              maxLength={200}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSaveTitle(e.target.value)}
              error={saveTitleError}
            />
            <Inline gap="3" justify="end">
              <Button type="button" variant="secondary" onClick={() => setSaveOpen(false)}>{t('analytics.saved.cancel')}</Button>
              <Button type="submit" variant="primary" disabled={saveMutation.isPending}>{t('analytics.saved.confirmSave')}</Button>
            </Inline>
          </Stack>
        </form>
      </Dialog>

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title={t('analytics.saved.deleteTitle')}
        message={t('analytics.saved.deleteMessage', { title: deleteTarget?.title ?? '' })}
        confirmLabel={t('analytics.saved.delete')}
        cancelLabel={t('analytics.saved.cancel')}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => {
          deleteMutation.reset();
          setDeleteTarget(null);
        }}
        isConfirming={deleteMutation.isPending}
      >
        {deleteMutation.isError && <Alert tone="danger">{t('analytics.saved.deleteError')}</Alert>}
      </ConfirmDialog>
    </>
  );
}

export default AnalyticsWorkbench;
